import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import {
  FileText,
  HelpCircle,
  Star,
  MessageSquare,
  ClipboardList,
  Settings,
  ArrowUpRight,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import {
  server_post_data,
  get_dashboard_overview,
} from "../ServiceConnection/serviceconnection.js";

const QUICK_LINKS = [
  {
    icon: FileText,
    label: "Blog Posts",
    sub: "Manage articles",
    href: "/blogs",
    color: "text-green-600",
    bg: "bg-green-500/10",
  },
  {
    icon: HelpCircle,
    label: "FAQ Manager",
    sub: "Edit questions",
    href: "/faqs",
    color: "text-orange-500",
    bg: "bg-orange-500/10",
  },
  {
    icon: Star,
    label: "Testimonials",
    sub: "Customer reviews",
    href: "/testimonials",
    color: "text-yellow-500",
    bg: "bg-yellow-500/10",
  },
  {
    icon: MessageSquare,
    label: "Contact Messages",
    sub: "View submissions",
    href: "/messages",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
  },
  {
    icon: ClipboardList,
    label: "Estimate Requests",
    sub: "View requests",
    href: "/estimates",
    color: "text-primary",
    bg: "bg-primary/10",
  },
  {
    icon: Settings,
    label: "Settings",
    sub: "Business info",
    href: "/settings",
    color: "text-purple-500",
    bg: "bg-purple-500/10",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const Overview = () => {
  const { toast } = useToast();

  const [dashData, setDashData] = useState(null);
  const [fetchLoader, setFetchLoader] = useState(false);

  // ─── Derived greeting ──────────────────────────────────────────────────
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const adminName = retrieveData("admin_name") || "Admin";

  // ─── Fetch ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("admin_id", retrieveData("admin_id") || "");

      const response = await server_post_data(get_dashboard_overview, fd);

      if (!response.data.error) {
        setDashData(response.data.message);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Stat cards config — values from API ──────────────────────────────
  const STATS = [
    {
      label: "Contact Messages",
      value: dashData?.total_contact_messages ?? "—",
      icon: MessageSquare,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      href: "/messages",
    },
    {
      label: "Estimate Requests",
      value: dashData?.total_estimate_requests ?? "—",
      icon: ClipboardList,
      color: "text-primary",
      bg: "bg-primary/10",
      href: "/estimates",
    },
    {
      label: "Blog Posts",
      value: dashData?.total_blog_posts ?? "—",
      icon: FileText,
      color: "text-green-500",
      bg: "bg-green-500/10",
      href: "/blogs",
    },
    {
      label: "Active FAQs",
      value: dashData?.total_active_faqs ?? "—",
      icon: HelpCircle,
      color: "text-orange-500",
      bg: "bg-orange-500/10",
      href: "/faqs",
    },
  ];

  const recentMessages = dashData?.recent_contact_messages || [];
  const recentEstimates = dashData?.recent_estimate_requests || [];

  // ─── UI ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-sidebar p-6 md:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 right-20 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
        <div className="relative">
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-primary">
            ACR Dashboard
          </p>
          <h1 className="text-2xl font-extrabold text-white md:text-3xl">
            {greeting}, {adminName} 👋
          </h1>
          <p className="mt-1.5 text-sm text-white/50">
            {now.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
            {" · "}Here's a summary of your website activity.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {STATS.map((s) => (
          <Link key={s.label} to={s.href}>
            <Card className="group cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-lg">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {s.label}
                    </p>
                    {fetchLoader ? (
                      <Loader2 className="mt-2 h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <p className="mt-1.5 text-3xl font-bold text-foreground">
                        {s.value}
                      </p>
                    )}
                  </div>
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.bg} transition-transform group-hover:scale-110`}
                  >
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent activity — 2 columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Contact Messages */}
        <Card>
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Recent Contact Messages
              </h2>
              <p className="text-xs text-muted-foreground">
                From the contact page form
              </p>
            </div>
            <Link
              to="/messages"
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <CardContent className="p-0">
            {fetchLoader && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!fetchLoader && recentMessages.length === 0 && (
              <p className="px-6 py-8 text-center text-xs text-muted-foreground">
                No messages yet.
              </p>
            )}
            {!fetchLoader &&
              recentMessages.map((msg, i) => {
                // action field = unread/read/replied (same as ContactSubmissions)
                const isUnread = !msg.action || msg.action === "unread";
                return (
                  <div
                    key={msg.primary_id || i}
                    className={`flex items-center gap-3 px-6 py-3.5 transition-colors hover:bg-muted/30 ${
                      i < recentMessages.length - 1
                        ? "border-b border-border"
                        : ""
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-white">
                      {msg.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`truncate text-sm text-foreground ${
                            isUnread ? "font-bold" : "font-medium"
                          }`}
                        >
                          {msg.name || "—"}
                        </p>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {timeAgo(msg.entry_date)}
                        </span>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {msg.message || msg.email || "—"}
                      </p>
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>

        {/* Recent Estimate Requests */}
        <Card>
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Recent Estimate Requests
              </h2>
              <p className="text-xs text-muted-foreground">
                From the homepage form
              </p>
            </div>
            <Link
              to="/estimates"
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <CardContent className="p-0">
            {fetchLoader && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!fetchLoader && recentEstimates.length === 0 && (
              <p className="px-6 py-8 text-center text-xs text-muted-foreground">
                No estimate requests yet.
              </p>
            )}
            {!fetchLoader &&
              recentEstimates.map((req, i) => {
                // booking_status field (same as EstimateRequests)
                const status = req.booking_status || "new";
                const statusColor =
                  {
                    new: "bg-primary/10 text-primary",
                    contacted: "bg-warning/10 text-warning",
                    scheduled: "bg-blue-500/10 text-blue-500",
                    completed: "bg-success/10 text-success",
                  }[status] || "bg-primary/10 text-primary";

                return (
                  <div
                    key={req.primary_id || i}
                    className={`flex items-center gap-3 px-6 py-3.5 transition-colors hover:bg-muted/30 ${
                      i < recentEstimates.length - 1
                        ? "border-b border-border"
                        : ""
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-white">
                      {req.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium text-foreground">
                          {req.name || "—"}
                        </p>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {timeAgo(req.entry_date)}
                        </span>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {req.vehicle_type || req.email || "—"}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${statusColor}`}
                    >
                      {status}
                    </span>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      </div>

      {/* Quick navigation */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            Quick Navigation
          </h2>
          <p className="text-xs text-muted-foreground">
            Jump to any section of your dashboard
          </p>
        </div>
        <CardContent className="p-5">
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="group flex flex-col items-center gap-2 rounded-2xl border border-border p-4 text-center transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md"
              >
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-xl ${link.bg} transition-transform group-hover:scale-110`}
                >
                  <link.icon className={`h-5 w-5 ${link.color}`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    {link.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {link.sub}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;